import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Form, InputNumber, Table, Typography } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React, { useCallback, useRef } from "react"

import { formListAddFocus } from "../../../../../utils/form/form-list-add-focus.ts"
import { budgetFormat } from "../../../../../utils/number/budget-format.ts"
import { validateNumber } from "../../../../../utils/validation/number.ts"
import {
  MAX_PAYMENT_PERCENTAGE_TOTAL,
  paymentPercentageTotal,
  paymentScheduleTotalError,
  validatePaymentScheduleTotal
} from "../../../../../utils/validation/payment-schedule-total.ts"
import type {
  TemplateDocumentFormData,
  TemplateDocumentScheduleItem
} from "../../../types/template-document-form-data.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const { Text } = Typography

interface PaymentScheduleBlockProps {
  form: FormInstance<TemplateDocumentFormData>
  index: number
}

export function PaymentScheduleBlock({
  form,
  index
}: PaymentScheduleBlockProps) {
  const key: keyof TemplateDocumentFormData = "payment_schedule"

  const isUpdatingRef = useRef(false)

  const hiddenColumns =
    Form.useWatch(["blocks", index, "config", "hidden_columns"], form) || []
  const paymentSchedule = Form.useWatch("payment_schedule", form) || []
  const projectTotal = Form.useWatch(["cost_summary", "total"], form) || 0

  const totalAmount = paymentSchedule.reduce(
    (sum: number, item) => sum + (+item?.amount || 0),
    0
  )

  const totalPercentage = paymentPercentageTotal(paymentSchedule)

  const handleAmountChange = useCallback(
    (fieldIndex: number, newAmount: number | null) => {
      if (isUpdatingRef.current || !projectTotal) return

      isUpdatingRef.current = true

      const percentage =
        newAmount && projectTotal
          ? Math.round((newAmount / projectTotal) * 100 * 100) / 100
          : 0

      const currentSchedule = form.getFieldValue(key) || []
      const updated = [...currentSchedule]
      updated[fieldIndex] = {
        ...updated[fieldIndex],
        amount: newAmount,
        percentage
      }

      form.setFieldsValue({ [key]: updated })

      setTimeout(() => {
        isUpdatingRef.current = false
      }, 0)
    },
    [form, key, projectTotal]
  )

  const handlePercentageChange = useCallback(
    (fieldIndex: number, newPercentage: number | null) => {
      if (isUpdatingRef.current || !projectTotal) return

      isUpdatingRef.current = true

      const amount =
        newPercentage && projectTotal
          ? Math.round((newPercentage / 100) * projectTotal * 100) / 100
          : 0

      const currentSchedule = form.getFieldValue(key) || []
      const updated = [...currentSchedule]
      updated[fieldIndex] = {
        ...updated[fieldIndex],
        percentage: newPercentage,
        amount
      }

      form.setFieldsValue({ [key]: updated })

      setTimeout(() => {
        isUpdatingRef.current = false
      }, 0)
    },
    [form, key, projectTotal]
  )

  const isPercentageExceeded = totalPercentage > 100

  return (
    <Form.List name={key} rules={[{ validator: validatePaymentScheduleTotal }]}>
      {(fields, { add, remove }) => (
        <>
          <Table
            pagination={false}
            dataSource={fields}
            scroll={{ x: "max-content" }}
            rowKey="key"
            bordered
            columns={[
              {
                hidden: hiddenColumns?.includes("milestone"),
                title: "Stage",
                className: "max-w-32",
                width: 250,
                dataIndex: "name",
                key: "milestone",
                render: (_, field) => (
                  <Form.Item<TemplateDocumentScheduleItem[]>
                    name={[field.name, "milestone"]}
                    style={{ margin: 0 }}
                    rules={[
                      { required: true, whitespace: true, message: "Required" }
                    ]}
                  >
                    <TextInput placeholder="Enter text" />
                  </Form.Item>
                )
              },
              {
                hidden: hiddenColumns?.includes("description"),
                title: "Description",
                dataIndex: "description",
                key: "description",
                render: (_, field) => (
                  <Form.Item<TemplateDocumentScheduleItem[]>
                    name={[field.name, "description"]}
                    style={{ margin: 0 }}
                  >
                    <TextInput placeholder="Enter description" />
                  </Form.Item>
                )
              },
              {
                hidden: hiddenColumns?.includes("amount"),
                title: "Amount",
                width: 200,
                className: "max-w-20",
                dataIndex: "amount",
                key: "amount",
                align: "right",
                render: (_, field) => (
                  <Form.Item<TemplateDocumentScheduleItem[]>
                    name={[field.name, "amount"]}
                    style={{ margin: 0 }}
                    rules={[{ validator: validateNumber }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="0"
                      suffix={"$"}
                      min={0}
                      max={99999999.99}
                      status={isPercentageExceeded ? "warning" : undefined}
                      precision={2}
                      onChange={(value) =>
                        handleAmountChange(field.name, value)
                      }
                    />
                  </Form.Item>
                )
              },
              {
                hidden: hiddenColumns?.includes("percentage"),
                title: "%",
                className: "max-w-16",
                width: 200,
                dataIndex: "percentage",
                key: "percentage",
                align: "right",
                render: (_, field) => (
                  <Form.Item<TemplateDocumentScheduleItem[]>
                    name={[field.name, "percentage"]}
                    style={{ margin: 0 }}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      status={isPercentageExceeded ? "warning" : undefined}
                      max={MAX_PAYMENT_PERCENTAGE_TOTAL}
                      placeholder="0"
                      suffix={"%"}
                      precision={2}
                      onChange={(value) =>
                        handlePercentageChange(field.name, value)
                      }
                    />
                  </Form.Item>
                )
              },
              {
                title: "",
                width: 50,
                render: (_, field) => (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                )
              }
            ]}
            summary={() => {
              const isMilestoneHidden = hiddenColumns?.includes("milestone")
              const isDescriptionHidden = hiddenColumns?.includes("description")
              const isAmountHidden = hiddenColumns?.includes("amount")
              const isPercentageHidden = hiddenColumns?.includes("percentage")

              const visibleLabelCols =
                (isMilestoneHidden ? 0 : 1) + (isDescriptionHidden ? 0 : 1)

              const hasLabelCols = visibleLabelCols > 0

              // If there are no label columns, "Total" will go to the first visible data cell
              let totalRendered = false
              let idx = 0

              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    {hasLabelCols && (
                      <Table.Summary.Cell
                        index={idx++}
                        colSpan={visibleLabelCols}
                      >
                        <strong>Total</strong>
                      </Table.Summary.Cell>
                    )}

                    {!isAmountHidden && (
                      <Table.Summary.Cell index={idx++} align="right">
                        <Flex vertical>
                          {!hasLabelCols &&
                            !totalRendered &&
                            (() => {
                              totalRendered = true
                              return <Text strong>Total</Text>
                            })()}
                          <Text
                            strong
                            type={isPercentageExceeded ? "warning" : undefined}
                          >
                            {budgetFormat(totalAmount)}
                          </Text>
                          <Text type="secondary">
                            of {budgetFormat(projectTotal)}
                          </Text>
                        </Flex>
                      </Table.Summary.Cell>
                    )}

                    {!isPercentageHidden && (
                      <Table.Summary.Cell index={idx++} align="right">
                        <Text
                          strong
                          type={isPercentageExceeded ? "warning" : undefined}
                        >
                          {totalPercentage?.toFixed(2)}%
                        </Text>
                      </Table.Summary.Cell>
                    )}

                    <Table.Summary.Cell index={idx++} />
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }}
            footer={() => (
              <Button
                type="dashed"
                onClick={() => {
                  formListAddFocus<TemplateDocumentFormData>(
                    add,
                    fields,
                    form,
                    [key],
                    ["milestone"]
                  )
                }}
                block
                icon={<PlusOutlined />}
              >
                Payment Stage
              </Button>
            )}
          />
          <Form.ErrorList
            errors={
              isPercentageExceeded
                ? [paymentScheduleTotalError(totalPercentage)]
                : []
            }
          />
        </>
      )}
    </Form.List>
  )
}
